import React from "react";
import { Search, List, LayoutGrid, Download, FileSpreadsheet, FileDown, X } from "lucide-react";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";

const FavoritesToolbar = ({
  title,
  totalCount,
  searchTerm,
  onSearchChange,
  viewMode,
  onViewModeChange,
  onExportCSV,
  onExportExcel,
}) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200/90 mb-5">
      <div>
        <h1 className="text-2xl font-bold text-explore tracking-tight m-0">{title}</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your bookmarked scholars, departments, and institutions with quick access and export.
        </p>
      </div>

      <div className="flex items-center gap-2.5 flex-wrap">
        {/* Search input with clear button */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <Input
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name, role, school..."
            className="pl-9 pr-8 h-9 bg-white text-xs border-slate-200 rounded-lg focus-visible:ring-explore"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* View mode toggle */}
        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={(val) => val && onViewModeChange(val)}
          className="bg-slate-100 p-0.5 rounded-lg border border-slate-200/80"
        >
          <ToggleGroupItem
            value="grid"
            aria-label="Grid view"
            className="h-8 px-2.5 data-[state=on]:bg-white data-[state=on]:shadow-2xs data-[state=on]:text-explore rounded-md text-slate-500 font-medium"
            title="Grid view"
          >
            <LayoutGrid size={15} />
          </ToggleGroupItem>
          <ToggleGroupItem
            value="list"
            aria-label="List view"
            className="h-8 px-2.5 data-[state=on]:bg-white data-[state=on]:shadow-2xs data-[state=on]:text-explore rounded-md text-slate-500 font-medium"
            title="List view"
          >
            <List size={15} />
          </ToggleGroupItem>
        </ToggleGroup>

        {/* Export dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border-slate-200 shadow-2xs rounded-lg"
              disabled={totalCount === 0}
            >
              <Download size={14} className="text-slate-500" />
              <span>Export</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44 bg-white shadow-xl border-slate-200 rounded-xl p-1">
            <DropdownMenuItem
              onClick={onExportCSV}
              className="text-xs cursor-pointer gap-2 py-2 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
            >
              <FileDown size={14} className="text-slate-400" />
              <span>Export as CSV</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onExportExcel}
              className="text-xs cursor-pointer gap-2 py-2 rounded-lg text-slate-700 font-medium hover:bg-slate-50"
            >
              <FileSpreadsheet size={14} className="text-emerald-600" />
              <span>Export as Excel</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default FavoritesToolbar;
